import { useCallback, useEffect, useRef } from 'react';
import { useNotificationPreferences } from '@socialflows/shared-lib';

interface PlaySoundOptions {
  type?: string;
  priority?: string;
}

function isInQuietHours(startTime: string | null, endTime: string | null): boolean {
  if (!startTime || !endTime) return false;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }
  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
}

export function useNotificationSound() {
  const soundRef = useRef<HTMLAudioElement | null>(null);
  const lastSoundTimeRef = useRef<number>(0);
  const { data: prefs } = useNotificationPreferences();
  const prefsRef = useRef(prefs);
  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  useEffect(() => {
    try {
      soundRef.current = new Audio('/sounds/notification.mp3');
      soundRef.current.volume = 0.3;
      soundRef.current.load();
    } catch {
      soundRef.current = null;
    }

    return () => {
      if (soundRef.current) {
        soundRef.current.pause();
        soundRef.current = null;
      }
    };
  }, []);

  const playSound = useCallback((options: PlaySoundOptions = {}) => {
    const currentPrefs = prefsRef.current;

    const soundEnabled = currentPrefs?.soundEnabled ?? true;
    const priorityFilter = currentPrefs?.soundPriorityFilter ?? ['high'];
    const minInterval = currentPrefs?.minSoundIntervalSecs ?? 30;
    const quietStart = currentPrefs?.quietHoursStart ?? null;
    const quietEnd = currentPrefs?.quietHoursEnd ?? null;
    const disabledTypes = currentPrefs?.disabledTypes ?? [];

    if (!soundEnabled) return;

    if (options.type && disabledTypes.includes(options.type)) return;

    if (options.priority && !priorityFilter.includes(options.priority)) return;

    if (isInQuietHours(quietStart, quietEnd)) return;

    const now = Date.now();
    if (now - lastSoundTimeRef.current < minInterval * 1000) return;
    lastSoundTimeRef.current = now;

    if (soundRef.current) {
      soundRef.current.currentTime = 0;
      soundRef.current.play().catch(() => {});
    }
  }, []);

  return { playSound };
}
